import { MutatorState } from '@aesop-fables/scrinium';
import { Box, Card, CardActionArea, Typography } from '@mui/material';
import { useState } from 'react';
import { LicenseAgreementData } from '../../../api/apis/LicenseAgreementApi';
import { Svgs } from '../../../assets/svg';
import { PricebookBundle } from '../../../models/PricebookData';
import { PaidByEnum } from '../../../models/LicenseData';
import { OrgSignUpStateData, State } from '../../../models/SignUpData';
import BundledTag from '../../../components/BundledTag';
import { useCancelContractToast } from '../../../components/CancelContract';
import PricebookInfoPopup from '../../../components/PricebookInfoPopup';
import {
  getActivePrice,
  getIntervalLabel,
  getAutoRenewLabel,
  getPriceAmountLabel,
  getPricebookPopupBody,
  getPricebookPopupTitle,
  isFreemiumPricebook,
} from '../../../helpers/pricebookUtils';
import LearnMoreDrawer from './LearnMoreDrawer';

interface PackageCardProps {
  pricebookBundle: PricebookBundle;
  selected: boolean;
  current?: boolean;
  setSelectedPricebook?: React.Dispatch<React.SetStateAction<PricebookBundle | undefined>>;
  currentLicense?: LicenseAgreementData;
  hasActiveAgreement?: boolean;
  updateSignUpState: MutatorState<OrgSignUpStateData>;
  signUpState?: State;
  multiplePricebooks: boolean;
  addingPackages?: boolean;
  addOnPricebook?: PricebookBundle;
  style?: React.CSSProperties;
}

const PackageCard: React.FC<PackageCardProps> = ({
  pricebookBundle,
  selected,
  current,
  setSelectedPricebook,
  currentLicense,
  hasActiveAgreement,
  updateSignUpState,
  signUpState,
  multiplePricebooks,
  addingPackages,
  addOnPricebook,
  style,
}) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const cancelContractToast = useCancelContractToast();

  const { pricebook, prices } = pricebookBundle;
  const isOrgPays = pricebook.paidBy === PaidByEnum.Org;

  const freemium = isFreemiumPricebook(pricebook);

  const isContracted = Boolean(currentLicense?.docStatus);

  const activePrice = getActivePrice(prices, { preferOneTime: true });
  const price = activePrice ? activePrice.price / 100 : 0;
  const formattedPrice = getPriceAmountLabel(activePrice);
  const intervalSuffix = getIntervalLabel(activePrice);
  const priceSubtitle =
    price === 0 ? (freemium ? 'No cost' : 'free') : getAutoRenewLabel(activePrice);

  const popupBody = getPricebookPopupBody(pricebook.internalAdminDescription);
  const popupTitle = getPricebookPopupTitle(pricebook.metadata, price);

  const cardVariant = isOrgPays ? 'pricebookAlternate' : 'pricebookPrimary';
  const isOwned = isContracted || Boolean(current);
  const selectable = addingPackages
    ? multiplePricebooks && !isOwned
    : multiplePricebooks && !hasActiveAgreement;
  const isChecked = multiplePricebooks && (selected || Boolean(current) || isContracted);
  const selectionClassName = [
    isChecked ? 'pricebook-selected' : 'pricebook-unselected',
    multiplePricebooks && isContracted ? 'pricebook-disabled' : '',
    multiplePricebooks ? 'pricebook-shadow' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const CheckmarkIcon = isContracted
    ? Svgs.IconCheckmarkCustomGray
    : isOrgPays
      ? Svgs.IconCheckmarkCustomAlternate
      : Svgs.IconCheckmarkCustomPrimary;

  const applySelection = () => {
    if (!setSelectedPricebook) return;
    setSelectedPricebook(prevState => (prevState !== pricebookBundle ? pricebookBundle : undefined));
    void updateSignUpState.action({
      state: {
        ...signUpState,
        pricebook: JSON.stringify(pricebookBundle),
      },
    });
  };

  const onSelectPricebook = () => {
    if (addingPackages) {
      if (!isOwned) {
        applySelection();
      }
      return;
    }
    if (hasActiveAgreement) {
      if (!isContracted) {
        cancelContractToast();
      }
      return;
    }
    applySelection();
  };

  return (
    <>
      <LearnMoreDrawer
        pricebookBundle={pricebookBundle}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        freemium={freemium}
      />
      <Card
        variant={cardVariant}
        className={selectionClassName}
        sx={{
          width: 335,
          minHeight: 260,
          ...style,
        }}>
        <CardActionArea
          onClick={multiplePricebooks || hasActiveAgreement ? onSelectPricebook : undefined}
          disableRipple={!selectable}
          sx={{
            height: '100%',
            cursor: selectable ? 'pointer' : 'default',
          }}>
          <Box className='pricebook-card-content'>
            {freemium && (
              <Box className='pricebook-card-bundled-tag'>
                <BundledTag addOnPricebook={addOnPricebook} />
              </Box>
            )}
            <img
              src={pricebook.artUrl}
              alt='Product Art'
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />

            <Box display='flex' alignItems='center' gap='8px' mt='10px'>
              <Typography variant='p16SemiBold' color='secondary.dark' textAlign='center'>
                {pricebook.pricebookName}
              </Typography>
              <PricebookInfoPopup
                title={popupTitle || undefined}
                body={popupBody}
                onLearnMore={() => setDrawerOpen(true)}
                iconSize={25}
                iconVariant='custom'
                placement='right'
                ariaLabel={`More about ${pricebook.pricebookName}`}
                passClickThrough
              />
            </Box>

            <Box mt='10px' width='100%' flex='1' minHeight='0' className='pricebook-bullets'>
              {pricebook.description.map((bullet, index) => (
                <Box key={index} display='flex' gap='8px'>
                  <Typography variant='p12' color='info.main'>
                    •
                  </Typography>
                  <Typography variant='p12' color='info.main'>
                    {bullet}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box textAlign='center' width='100%' flexShrink={0} pt='10px'>
              <Typography component='div' variant='p20SemiBold' color='secondary.dark'>
                <Typography component='span' variant='p20SemiBold' color='info.main'>
                  $
                </Typography>
                {formattedPrice}
                {intervalSuffix && (
                  <Typography component='span' variant='p16' color='secondary.main'>
                    {intervalSuffix}
                  </Typography>
                )}
              </Typography>
              <Typography component='div' variant='p12Light' color='secondary.main'>
                {priceSubtitle}
              </Typography>
            </Box>

            {isChecked && (
              <Box className='pricebook-card-checkmark'>
                <CheckmarkIcon />
              </Box>
            )}
          </Box>
        </CardActionArea>
      </Card>
    </>
  );
};

export default PackageCard;
